import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getDossier } from "../api/dossier/getDossier";
import { getDossiersForContact } from "../api/dossier/getDossiersForContact";
import { getMyDossiers } from "../api/dossier/getMyDossiers";
import { PageDirectoryEntryName } from "../api/PageDirectory";
import {
  filterActivatedDossiers,
  filterActiveDossiers,
  filterOpenDossiers,
  filterUncancelledDossiers,
  getLatestOpenDossier,
} from "../helpers/dossiers";
import { useAuth } from "../providers/AuthProvider";
import { Dossier } from "../types/Dossier";

interface UseDossierActivationCheckProps {
  dossierId: string | undefined;
  activeDossiersOnly?: boolean;
  redirectTo?: PageDirectoryEntryName;
}

interface UseDossierActivationCheckReturn {
  navigationCheckComplete: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  lastestOpenDossier: Dossier | null;
  openDossiers: Dossier[];
  activatedDossiers: Dossier[];
  activeDossiers: Dossier[];
  uncancelledDossiers: Dossier[];
}

/**
 * Hook to check if a dossier is activated and handle navigation accordingly.
 * Automatically fetches dossiers based on app scope (external vs internal).
 *
 * @param dossierId - The ID of the dossier to check
 * @param redirectTo - Optional page to redirect to if dossier is not activated (defaults to "home")
 *
 * @returns Object containing navigationCheckComplete flag, loading states, success state, and dossiers data
 *
 * @example
 * ```tsx
 * const { navigationCheckComplete, isLoading, isSuccess, dossiers } = useDossierActivationCheck({
 *   dossierId,
 *   redirectTo: "home" // optional, defaults to "home"
 * });
 *
 * return (
 *   <PageContainer loading={isLoading || !navigationCheckComplete}>
 *     {isSuccess && navigationCheckComplete && (
 *       // Your component content here
 *     )}
 *   </PageContainer>
 * );
 * ```
 */
export const useDossierActivationCheck = ({
  dossierId,
  activeDossiersOnly = false,
  redirectTo = "home",
}: UseDossierActivationCheckProps): UseDossierActivationCheckReturn => {
  const navigate = useNavigate();
  const { getScopedPageUrl, inAppScope } = useAuth();
  const [navigationCheckComplete, setNavigationCheckComplete] = useState(false);

  const isExternal = inAppScope("external");

  // Use different data fetching based on app scope
  const dossiersQuery = getMyDossiers({ disabled: !isExternal });
  const dossierQuery = getDossier(dossierId ?? "", { disabled: isExternal });
  const dossiersForContactQuery = getDossiersForContact(
    dossierQuery.data?.crmContactId ?? "",
    { disabled: isExternal || !dossierQuery.data?.crmContactId }
  );

  // Handle errors
  if (dossiersQuery?.isError) {
    throw dossiersQuery.error;
  }
  if (dossierQuery?.isError) {
    throw dossierQuery.error;
  }
  if (dossiersForContactQuery?.isError) {
    throw dossiersForContactQuery.error;
  }

  // Get loading and success states
  const isLoading = isExternal
    ? dossiersQuery?.isLoading
    : dossierQuery?.isLoading || dossiersForContactQuery?.isLoading;
  const isSuccess = isExternal
    ? dossiersQuery?.isSuccess
    : dossierQuery?.isSuccess && dossiersForContactQuery?.isSuccess;

  // Get dossiers data
  const dossiers = isExternal
    ? dossiersQuery?.data || []
    : dossiersForContactQuery?.data
    ? dossiersForContactQuery.data
    : [];

  useEffect(() => {
    if (isSuccess && dossierId) {
      const activatedDossiers = filterActivatedDossiers(dossiers);
      const activeDossiers = filterActiveDossiers(dossiers);

      const isDossierActivated = activatedDossiers.some(
        (dossier) => dossier.id === dossierId
      );
      const isDossierActive = activeDossiers.some(
        (dossier) => dossier.id === dossierId
      );
      const activeCheck = activeDossiersOnly ? isDossierActive : true;

      if ((!activeCheck || !isDossierActivated) && isExternal) {
        navigate(getScopedPageUrl(redirectTo), { replace: true });
      } else {
        setNavigationCheckComplete(true);
      }
    }
  }, [isSuccess, dossierId]);

  return {
    navigationCheckComplete,
    isLoading: isLoading,
    isSuccess: isSuccess,
    lastestOpenDossier: getLatestOpenDossier(dossiers),
    openDossiers: filterOpenDossiers(dossiers),
    activatedDossiers: filterActivatedDossiers(dossiers),
    activeDossiers: filterActiveDossiers(dossiers),
    uncancelledDossiers: filterUncancelledDossiers(dossiers),
  };
};
